import moment from "moment";
import {
  PERIOD,
  compareLines,
  currentTemplateVersion,
  evaluateTemplate,
  lineErrors,
  versionLabel,
} from "../SalaryTemplates/salaryTemplateData";
import { formatRupees } from "../SalaryComponents/salaryComponentData";
import { APPROVAL_LEVELS, effectiveRange, requestStatus, scaleLinesToGross } from "../AssignSalary/assignSalaryData";
import { customComponentRows } from "../AssignSalary/assignSalaryApi";

// Helpers for Salary Revision (its data comes from revisionApi.js).

export const REASONS = ["Annual increment", "Promotion", "Market correction", "Template update", "Correction"];

export const STRUCTURE_OPTIONS = [
  { value: "keep", label: "Keep current structure", helper: "Each employee keeps their template or customization" },
  { value: "change", label: "Change template", helper: "Move everyone to one template" },
  { value: "customize", label: "Customize", helper: "One common custom structure" },
];

export const CHANGE_OPTIONS = [
  { value: "percent", label: "% increase", helper: "e.g. annual increment" },
  { value: "fixed", label: "Fixed increase", helper: "Same amount for all" },
  { value: "set", label: "Set new salary", helper: "Enter each gross" },
  { value: "none", label: "No change", helper: "Structure change only" },
];

export const TEMPLATE_CHANGE_OPTIONS = [
  { value: "none", label: "No change", helper: "Structure change only" },
  { value: "percent", label: "% increase", helper: "Combine with a hike" },
  { value: "fixed", label: "Fixed increase", helper: "Same amount for all" },
  { value: "set", label: "Set new salary", helper: "Enter each gross" },
];

export { APPROVAL_LEVELS, effectiveRange, requestStatus, PERIOD, versionLabel, formatRupees };

export const formatDay = (value) => (value ? moment(value).format("DD MMM YYYY") : "–");

// ---------- Helpers ----------

export const OPEN_STATUSES = ["Draft", "Pending", "Rejected"];

export const openRevisionByEmployee = (revisions, exceptId) =>
  new Map(
    revisions
      .filter((revision) => OPEN_STATUSES.includes(revision.status) && revision.id !== exceptId)
      .flatMap((revision) => revision.employees.map((row) => [row.employee_id, revision])),
  );

const sameId = (a, b) => a != null && b != null && String(a) === String(b);
export const templateById = (templates, id) => templates.find((template) => sameId(template.id, id));
export const versionOf = (templates, id, version) => templateById(templates, id)?.versions.find((item) => item.version === version);
export const employeeOfList = (employees, id) => employees.find((employee) => sameId(employee.id, id));

// Customized: own rows, or the server says so.
export const isCustomized = (employee) => Boolean(employee?.customized || employee?.lines);

export const employeeLabel = (templates, employee) => {
  const name = templateById(templates, employee.template_id)?.name || employee.template_name;
  return name ? `${name}${employee.version ? ` ${versionLabel(employee.version)}` : ""}` : "no salary yet";
};

// Why an employee can't be picked (or null).
export const blockReason = (employee, open) => {
  if (!employee) return "Not found";
  if (employee.eligible === false) return employee.reason || "Can't be revised now";
  if (employee.eligible !== true && !employee.gross) return "Not configured, use Assign Salary";
  const revision = open.get(employee.id);
  if (revision) return `Open revision ${revision.number} (${revision.status})`;
  return null;
};

export const currentLines = (templates, employee) =>
  employee.lines || employee.current_lines || versionOf(templates, employee.template_id, employee.version)?.lines || [];

// The new salary for one employee under the form's settings.
export const applyChange = (form, gross) => {
  const value = Number(form.change_value);
  if (form.change_mode === "percent") return Number.isFinite(value) ? Math.round(gross * (1 + value / 100)) : gross;
  if (form.change_mode === "fixed") return Number.isFinite(value) ? gross + value : gross;
  return gross;
};

// Where an employee ends up: template, version, lines.
export const targetFor = (form, templates, employee) => {
  if (form.kind === "template" || form.structure === "change") {
    const template = templateById(templates, form.template_id);
    const version = form.kind === "template" ? form.target_version : template ? currentTemplateVersion(template).version : null;
    return { template_id: form.template_id, version, lines: null };
  }
  if (form.structure === "customize") {
    const template = templateById(templates, form.template_id);
    return { template_id: form.template_id, version: template ? currentTemplateVersion(template).version : null, lines: form.common_lines };
  }
  return { template_id: employee.template_id, version: employee.version, lines: employee.lines || employee.current_lines || null };
};

export const linesFor = (templates, target) => target.lines || versionOf(templates, target.template_id, target.version)?.lines || [];

// The target's rows at the new gross. Fixed earnings that add up to the old
// gross (a kept customization, an all-fixed template) are scaled to the new
// one, as Assign Salary does; rows that already add up are left as they are.
export const linesAtGross = (catalog, templates, target, gross) => {
  const lines = linesFor(templates, target);
  if (!(gross > 0) || !lines.length) return lines;
  return scaleLinesToGross(catalog, lines, gross) || lines;
};

// One employee's rows at their new gross: their own Customize edit
// (row.lines) when there is one, else the target's rows (scaled when needed).
export const rowLines = (catalog, templates, form, employee, row, gross) => {
  if (row?.lines) return gross > 0 ? scaleLinesToGross(catalog, row.lines, gross) || row.lines : row.lines;
  return linesAtGross(catalog, templates, targetFor(form, templates, employee), gross);
};

// The employee's components for the request (same shape as Assign Salary's
// customized employees), or null when the server's own rows already hold:
// sent for a Customize edit, or rows scaled to the new gross.
export const rowComponents = (catalog, templates, form, employee, row) => {
  const target = targetFor(form, templates, employee);
  const plain = linesFor(templates, target);
  const lines = rowLines(catalog, templates, form, employee, row, rowNewGross(form, employee, row));
  if (!lines.length || (!row?.lines && JSON.stringify(lines) === JSON.stringify(plain))) return null;
  const base = versionOf(templates, target.template_id, target.version)?.lines || plain;
  return customComponentRows(base, lines);
};

export const structureLabel = (form, employee, row) => {
  if (row?.lines) return { label: "Customized · edited", tone: "purple" };
  if (!isCustomized(employee)) return { label: "Template Linked", tone: "slate" };
  if (form.kind === "template" || form.structure !== "keep") return { label: "Customized · replaced", tone: "amber" };
  return { label: "Customized · kept", tone: "purple" };
};

export const changeText = (revision, templates) => {
  const target = revision.employees[0]?.to;
  const template = templateById(templates, target?.template_id || revision.template_id);
  const templateName = template?.name || revision.template_name || target?.template_name || "template";
  const { mode, value } = revision.change;
  const period = PERIOD[template?.pay_basis || revision.salary_type] || "month";
  const amount =
    mode === "percent" ? `+${value}% increase` : mode === "fixed" ? `+${formatRupees(Number(value))} per ${period}` : mode === "set" ? "Set new salary" : "No change to gross";
  if (revision.kind === "template") return { main: `Move to template ${versionLabel(revision.target_version)}`, sub: amount };
  if (revision.kind === "import") return { main: "Set new salary", sub: "From imported file" };
  if (revision.structure === "change") return { main: `Change template → ${templateName}`, sub: amount };
  if (revision.structure === "customize") return { main: amount === "No change to gross" ? "Customize structure" : amount, sub: "Common custom structure" };
  return { main: mode === "set" ? "Set new salary" : amount, sub: "Keep current structure" };
};

const sumOf = (rows, key) => rows.reduce((total, row) => total + Number(row[key] || 0), 0);
const hasAmounts = (revision, key) => revision.employees.some((row) => Number(row[key]) > 0);

// The flat row the list and CustomFilter work on (API revisions).
export const toRevisionRow = (templates, revision) => {
  const status = requestStatus(revision);
  const change = changeText(revision, templates);
  const count = revision.employees.length || revision.employee_count || 0;
  const single = count === 1 ? revision.employees[0] || { name: revision.employee_names[0] || "" } : null;
  const fromVersions = [...new Set(revision.employees.map((row) => row.from.version).filter(Boolean))].sort();
  const sourceLabel = fromVersions.length ? fromVersions.map(versionLabel).join(", ") : revision.source_version ? versionLabel(revision.source_version) : "";
  const title =
    revision.title ||
    (revision.kind === "template"
      ? `${revision.template_name || "Template"} · ${sourceLabel ? `${sourceLabel} → ` : ""}${versionLabel(revision.target_version)}`
      : single?.name || `${count} employee${count === 1 ? "" : "s"}`);
  return {
    id: revision.id,
    revision: title,
    kind: revision.kind === "template" ? "From template" : revision.kind === "import" ? "Import" : "Employees",
    detail: [
      single ? [single.code, single.department].filter(Boolean).join(" · ") || "1 employee" : `${count} employees`,
      revision.status === "Draft" ? "not submitted" : revision.number,
    ].filter(Boolean).join(" · "),
    number: revision.number,
    names: revision.employee_names.join(" "),
    change: change.main,
    change_detail: change.sub,
    // List rows name the employees without their amounts, so the request's
    // totals are used until the employees' amounts are loaded.
    current: hasAmounts(revision, "current_gross") ? sumOf(revision.employees, "current_gross") : revision.total_current ?? 0,
    gross: hasAmounts(revision, "new_gross") ? sumOf(revision.employees, "new_gross") : revision.total_new ?? 0,
    period: PERIOD[revision.salary_type] || "month",
    effective: revision.effective_from ? formatDay(revision.effective_from) : effectiveRange(revision),
    status: status.label,
    status_key: status.key,
    status_tone: status.tone,
    status_detail: status.detail,
    // The card a row counts under: Upcoming / Applied are Approved.
    card: revision.status === "Approved" ? "Approved" : revision.status,
    mine: Boolean(revision.pending_mine),
  };
};

// ---------- Form ----------

// The first payroll date that isn't processed yet: the server's, else the
// 1st of next month (the server still refuses a back-dated revision).
export const defaultEffectiveFrom = (minDate) => minDate || moment().add(1, "month").startOf("month").format("YYYY-MM-DD");

export const emptyRevisionForm = (kind, { ids = [], minDate = "" } = {}) => ({
  revision_id: null,
  kind,
  structure: "keep",
  change_mode: kind === "template" ? "none" : "percent",
  change_value: kind === "template" ? "" : "8",
  reason: kind === "template" ? "Template update" : "Annual increment",
  comment: "",
  template_id: null,
  target_version: null,
  from_versions: [],
  common_lines: null,
  effective_from: defaultEffectiveFrom(minDate),
  rows_for: null,
  rows: ids.map((id) => ({ employee_id: id, new_gross: "", manual: false })),
});

// Template mode: every employee on an older version of the template, with
// Template Linked ones ticked and Customized ones not.
// Versions in order, unknown (null) last.
export const byVersion = (a, b) => (a == null ? 1 : b == null ? -1 : a - b);
export const versionText = (version) => (version == null ? "Version unknown" : versionLabel(version));

export const withTemplateRows = (form, templates, employees, revisions, exceptId) => {
  const template = templateById(templates, form.template_id);
  if (!template) return { ...form, rows: [], from_versions: [], rows_for: null };
  const target = form.target_version || currentTemplateVersion(template).version;
  const open = openRevisionByEmployee(revisions, exceptId);
  // Everyone on the template up to the target version: older versions move
  // to the target, the target's own employees get the salary change only,
  // and an employee whose version isn't known is listed too (version null).
  const onOlder = employees.filter(
    (employee) => sameId(employee.template_id, template.id) && (employee.version == null || employee.version <= target),
  );
  const versions = [...new Set(onOlder.map((employee) => employee.version ?? null))].sort(byVersion);
  return {
    ...form,
    target_version: target,
    from_versions: versions,
    rows_for: String(template.id),
    rows: onOlder.map((employee) => ({
      employee_id: employee.id,
      selected: !isCustomized(employee) && !blockReason(employee, open),
      new_gross: "",
      manual: false,
    })),
  };
};

export const revisionToForm = (revision) => {
  const isTemplate = revision.kind === "template";
  const fromVersions = [...new Set(revision.employees.map((row) => row.from.version).filter(Boolean))].sort();
  return {
    revision_id: revision.id,
    kind: isTemplate ? "template" : "employees",
    structure: revision.structure,
    change_mode: revision.change.mode,
    change_value: revision.change.value,
    reason: revision.reason,
    comment: revision.comment,
    template_id: isTemplate ? revision.source_template_id || revision.template_id : revision.structure !== "keep" ? revision.template_id || revision.employees[0]?.to.template_id || null : null,
    target_version: revision.target_version || null,
    from_versions: fromVersions.length ? fromVersions : revision.source_version ? [revision.source_version] : [],
    common_lines: revision.structure === "customize" ? revision.employees[0]?.revised_lines || revision.employees[0]?.lines || null : null,
    effective_from: revision.effective_from || revision.employees[0]?.effective_from || defaultEffectiveFrom(""),
    // Template mode keeps the saved rows; the template's other employees
    // aren't added back.
    rows_for: isTemplate ? String(revision.source_template_id || revision.template_id || "") : null,
    rows: revision.employees.map((row) => ({
      employee_id: row.employee_id,
      selected: true,
      new_gross: String(row.new_gross ?? ""),
      manual: revision.change.mode === "set" || Boolean(row.lines) || (row.new_gross != null && row.new_gross !== applyChange({ change_mode: revision.change.mode, change_value: revision.change.value }, row.current_gross)),
      // An employee saved with their own structure (common Customize aside).
      lines: revision.structure !== "customize" && row.lines ? row.lines.map((line) => ({ ...line })) : null,
    })),
  };
};

// Rows that take part (template mode: ticked and on a chosen version).
export const activeRows = (form, employees) =>
  form.kind === "template"
    ? form.rows.filter((row) => row.selected && form.from_versions.includes(employeeOfList(employees, row.employee_id)?.version ?? null))
    : form.rows;

export const rowNewGross = (form, employee, row) =>
  row.manual || form.change_mode === "set" ? Number(row.new_gross || employee.gross) : applyChange(form, employee.gross);

// minDate: the first date a revision can start, when known. The server
// repeats every check.
export const checkRevisionForm = (form, { catalog, templates, employees, revisions, minDate }) => {
  const errors = {};
  const perEmployee = {};
  const open = openRevisionByEmployee(revisions, form.revision_id);
  const rows = activeRows(form, employees);
  if (!form.reason) errors.reason = "Select a reason";
  if ((form.kind === "template" || form.structure !== "keep") && !form.template_id) errors.template = "Select a template";
  if (form.structure === "customize" && form.kind !== "template" && !form.common_lines) errors.common = "Set up the common structure";
  if (["percent", "fixed"].includes(form.change_mode) && !(Number(form.change_value) > 0)) errors.change_value = form.change_mode === "percent" ? "Enter a percentage above 0" : "Enter an amount above 0";
  if (!form.effective_from) errors.effective_from = "Pick the effective date";
  else if (minDate && moment(form.effective_from).isBefore(minDate, "day")) errors.effective_from = `Can't be before ${formatDay(minDate)} (payroll is processed up to then)`;
  if (!rows.length) errors.rows = form.kind === "template" ? "Tick at least one employee" : "Add at least one employee";
  let earningsOk = true;
  rows.forEach((row) => {
    const employee = employeeOfList(employees, row.employee_id);
    if (!employee) return;
    const problems = [];
    const block = blockReason(employee, open);
    if (block) problems.push(block);
    const gross = rowNewGross(form, employee, row);
    if (!(gross > 0)) problems.push("Enter a new gross above 0");
    const target = targetFor(form, templates, employee);
    const targetTemplate = templateById(templates, target.template_id);
    const currentType = templateById(templates, employee.template_id)?.pay_basis || employee.salary_type;
    if (targetTemplate && currentType && targetTemplate.pay_basis !== currentType) problems.push(`Salary type is ${currentType}, template is ${targetTemplate.pay_basis}`);
    const lines = rowLines(catalog, templates, form, employee, row, gross);
    if (gross > 0 && lines.length) {
      const result = evaluateTemplate(catalog, lines, gross);
      if (!result.balanced || result.unresolved.length || Object.keys(lineErrors(catalog, lines)).length) {
        earningsOk = false;
        problems.push("Earnings don't add up to gross");
      }
    }
    if (problems.length) perEmployee[row.employee_id] = problems;
  });
  return { errors, perEmployee, earningsOk, rows };
};

// What moving from a version to the target changes, for the side card.
// null when either version's rows aren't loaded.
export const versionChanges = (catalog, templates, templateIdValue, fromVersion, toVersion) => {
  const from = versionOf(templates, templateIdValue, fromVersion);
  const to = versionOf(templates, templateIdValue, toVersion);
  if (!from?.lines?.length || !to?.lines?.length) return null;
  return compareLines(catalog, from.lines, to.lines).filter((item) => item.change !== "Unchanged");
};
